import { useEffect, type ReactNode } from "react";
import { CircleHelp, History, Minus, Plus, RotateCcw, Settings2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DEFAULT_EDITOR_PREFS,
  FONT_SIZE_MAX,
  FONT_SIZE_MIN,
  FONT_SIZE_STEP,
  setEditorPrefs,
  useEditorPrefs,
} from "@/lib/editorPrefs";
import { languageLabel, type Language } from "@/lib/runtimes/registry";
import { useDismissableMenu } from "@/lib/useDismissableMenu";
import { cn } from "@/lib/utils";

/**
 * FEAT-18 — the strip above the code editor: which language this question is
 * in, then Reset, Settings and Help.
 *
 * THE LANGUAGE IS A LABEL, NEVER A CONTROL. The recruiter sets the language
 * per question and the candidate cannot change it (a locked decision), so
 * there is no dropdown here and there must never be one —
 * `CodeQuestion.toolbar.test.ts` fails on a `select` / combobox / listbox.
 *
 * The settings panel holds BUTTONS ONLY. A text field or a native `<select>`
 * would sit outside the Monaco editor, where the anti-cheat controller blocks
 * and RECORDS Ctrl+C / Ctrl+V and a single recorded shortcut flags the
 * attempt. It opens in place (no portal — the candidate subtree is
 * `translate="no"`, App.tsx) and never mentions Escape: in a real test Escape
 * also leaves fullscreen, which flags the attempt too.
 *
 * All wording here is a placeholder (Emilia's).
 */
interface EditorToolbarProps {
  language: Language;
  /** There is a starter to go back to (absent on a locked, submitted position). */
  canReset: boolean;
  /** The editor is not there yet, or a Run is still writing results. */
  resetDisabled: boolean;
  onResetClick: () => void;
  onHelpClick: () => void;
  /** The fullscreen gate is up: nothing of ours may sit on top of it. */
  overlaysBlocked: boolean;
  /** FEAT-31: this attempt stores a code replay — a caption says so for as
   *  long as the editor is on screen (the EF-8 "a photo is taken" idiom). */
  recording?: boolean;
}

function Segment({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      onClick={onClick}
      className={cn(
        "flex-1 cursor-pointer rounded px-2.5 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        pressed
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      {children}
    </button>
  );
}

function SettingRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

export function EditorToolbar({
  language,
  canReset,
  resetDisabled,
  onResetClick,
  onHelpClick,
  overlaysBlocked,
  recording = false,
}: EditorToolbarProps) {
  const prefs = useEditorPrefs();
  const { open, setOpen, containerRef } = useDismissableMenu();
  useEffect(() => {
    if (overlaysBlocked) setOpen(false);
  }, [overlaysBlocked, setOpen]);

  const isDefault =
    prefs.fontSize === DEFAULT_EDITOR_PREFS.fontSize &&
    prefs.theme === DEFAULT_EDITOR_PREFS.theme &&
    prefs.wordWrap === DEFAULT_EDITOR_PREFS.wordWrap;

  return (
    <div
      className="flex items-center justify-between gap-2 border-b border-border bg-card px-3 py-1.5"
      data-testid="editor-toolbar"
    >
      <span
        className="min-w-0 truncate font-mono text-xs text-muted-foreground"
        data-testid="editor-language"
        title="The language is set for this question and cannot be changed."
      >
        {languageLabel(language)}
      </span>
      {recording && (
        // Placeholder copy (wording is Emilia's). A label, never a control.
        <span
          className="flex min-w-0 flex-1 items-center gap-1.5 text-xs text-muted-foreground"
          data-testid="editor-replay-caption"
          title="How your code changes is recorded for this test, including text you paste or delete."
        >
          <History className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span className="truncate">Your edits here are recorded</span>
        </span>
      )}
      <div className="flex shrink-0 items-center gap-1">
        {canReset && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={onResetClick}
            disabled={resetDisabled}
            aria-label="Reset to the starting code"
            className="cursor-pointer px-2"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Reset</span>
          </Button>
        )}
        <div ref={containerRef} className="relative">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setOpen((v) => !v)}
            aria-label="Editor settings"
            aria-expanded={open}
            aria-haspopup="dialog"
            className="cursor-pointer px-2"
          >
            <Settings2 className="h-4 w-4" aria-hidden="true" />
            <span className="hidden sm:inline">Settings</span>
          </Button>
          {open && !overlaysBlocked && (
            <div
              role="dialog"
              aria-label="Editor settings"
              data-testid="editor-settings"
              className="absolute right-0 top-full z-30 mt-1 w-64 space-y-3 rounded-md border border-border bg-card p-3 text-card-foreground shadow-md"
            >
              <SettingRow label="Text size">
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setEditorPrefs({ fontSize: prefs.fontSize - FONT_SIZE_STEP })
                    }
                    disabled={prefs.fontSize <= FONT_SIZE_MIN}
                    aria-label="Smaller text"
                    className="h-7 w-7 cursor-pointer p-0"
                  >
                    <Minus className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                  <span
                    className="w-12 text-center font-mono text-xs tabular-nums"
                    aria-live="polite"
                    data-testid="editor-font-size"
                  >
                    {prefs.fontSize} px
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setEditorPrefs({ fontSize: prefs.fontSize + FONT_SIZE_STEP })
                    }
                    disabled={prefs.fontSize >= FONT_SIZE_MAX}
                    aria-label="Larger text"
                    className="h-7 w-7 cursor-pointer p-0"
                  >
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </SettingRow>
              <SettingRow label="Theme">
                <div
                  role="group"
                  aria-label="Theme"
                  className="flex w-32 rounded-md bg-muted p-0.5"
                >
                  <Segment
                    pressed={prefs.theme === "vs-dark"}
                    onClick={() => setEditorPrefs({ theme: "vs-dark" })}
                  >
                    Dark
                  </Segment>
                  <Segment
                    pressed={prefs.theme === "vs"}
                    onClick={() => setEditorPrefs({ theme: "vs" })}
                  >
                    Light
                  </Segment>
                </div>
              </SettingRow>
              <SettingRow label="Wrap long lines">
                <div
                  role="group"
                  aria-label="Wrap long lines"
                  className="flex w-32 rounded-md bg-muted p-0.5"
                >
                  <Segment
                    pressed={!prefs.wordWrap}
                    onClick={() => setEditorPrefs({ wordWrap: false })}
                  >
                    Off
                  </Segment>
                  <Segment
                    pressed={prefs.wordWrap}
                    onClick={() => setEditorPrefs({ wordWrap: true })}
                  >
                    On
                  </Segment>
                </div>
              </SettingRow>
              <div className="flex items-center justify-between gap-2 border-t border-border pt-2">
                <button
                  type="button"
                  onClick={() => setEditorPrefs(DEFAULT_EDITOR_PREFS)}
                  disabled={isDefault}
                  className="cursor-pointer rounded text-xs text-muted-foreground underline-offset-4 transition-colors hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-default disabled:opacity-50 disabled:hover:no-underline"
                >
                  Back to defaults
                </button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setOpen(false)}
                  className="h-7 cursor-pointer"
                >
                  Done
                </Button>
              </div>
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onHelpClick}
          aria-label="Code editor help"
          className="cursor-pointer px-2"
        >
          <CircleHelp className="h-4 w-4" aria-hidden="true" />
          <span className="hidden sm:inline">Help</span>
        </Button>
      </div>
    </div>
  );
}
